import { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { MRT_Cell, MRT_ColumnDef, MRT_Localization, MRT_Row, MRT_RowVirtualizer } from "material-react-table";
import { Table } from "@/components/elements/Table";
import { Select } from "@/components/elements/Select";
import { Input } from "@/components/elements/Input/Input";
import { Switch } from "@/components/elements/Switch";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { LearnerPlanStatusSelect } from "@/features/learner/components/learner-list/LearnerPlanStatusSelect";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { useGetMunicipalityCode } from "@/hooks/useGetMunicipalityCode";
import { useEnum } from "@/api/services/global/enum";
import { useGetListCompanyByRso } from "@/api/services/main/company";
import { useLearnerIndustryOptions } from "@/features/learner/hook/useLearnerIndustryOptions";
import { getLearningProgressExamListApi, useLearningProgressExamList } from "@/api/services/main/learning-progress";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MASTER_DATA_CACHE_TIME } from "@/constants/common";
import { IconCalendar, IconDeleteOutline, IconExpandMore, IconFilterList } from "@/assets/icons";
import { background, black, border, divider, red, text } from "@/theme/colors";
import { EXAM_METHOD, EXAM_STATUS, USER_TYPE } from "@/types/enum";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { TOption } from "@/types/common";
import { dayjs } from "@/utils/dayjs";
import { Dayjs, isDayjs } from "dayjs";
import { calculateResidencePeriods, getNextOrCurrentMonday } from "@/features/learner/utils/calculates";
import { STATUS_OPTIONS, TBulkImportRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import { isLastScoreValueInvalid } from "@/features/learner/components/learner-bulk-import/bulk-import-payload";

type Props = {
  rows: TBulkImportRow[];
  onChangeRow: (id: number, patch: Partial<TBulkImportRow>) => void;
  onDeleteRow: (id: number) => void;
  // 選択された行のキー(getRowId と同じ文字列)を親へ通知。一括変更などの対象行に利用する。
  // 数値化せず文字列キーで渡す: visa の id が数値文字列(bigint)で返るケースでも突合がズレないようにするため。
  onSelectionChange?: (selectedKeys: string[]) => void;
  // 必須未入力セルの { rowId: [カラムID...] }。該当セルを赤枠表示する。
  fieldErrors?: Record<number, string[]>;
  // 先頭の不備行へスクロールする指示 (nonce 変化で再スクロール)。
  focusTarget?: { rowId: number; nonce: number } | null;
  // 選択(チェックボックス)を解除する指示。値が変わるたびに全選択をクリアする (一括変更の確定後など)。
  clearSelectionNonce?: number;
};

// 行選択チェックボックスのツールチップを日本語化
const SELECT_LOCALIZATION = {
  toggleSelectAll: "すべて選択",
  toggleSelectRow: "選択",
} as Partial<MRT_Localization> as MRT_Localization;

// フォーカス時のオレンジ枠 (Select共通の ::after リング) を非表示にする
const SELECT_NO_FOCUS_RING = { "&.Mui-focused::after": { display: "none" } };

// Switch を中央寄せ。FormControlLabel の既定マージン (marginRight:16/marginLeft:-11) を打ち消す。
// さらに Switch コンポーネントが常に描画する空ラベル枠を消す: ラベル分の幅があるとスイッチが枠の左に寄り、列中央からずれるため。
const SWITCH_CELL_SX = { "& .MuiFormControlLabel-root": { m: 0 }, "& .MuiFormControlLabel-label": { display: "none" } };

// 全ヘッダー共通: 14px + 必須(*)が列幅で見切れないよう content-wrapper の overflow を可視にする。
// 列単位の muiTableHeadCellProps は table 単位の sx を上書きする(shallow merge)ため、各列の sx にも展開して付与する。
const HEAD_BASE_SX = { fontSize: "14px !important", "& .Mui-TableHeadCell-Content-Wrapper": { overflow: "visible" } };

// ヘッダー内容を左寄せにする。Header(SortHeader等)未指定で既定の space-between により右寄り表示になる列に付与する
const LEFT_HEAD_SX = { ...HEAD_BASE_SX, "& .Mui-TableHeadCell-Content": { justifyContent: "flex-start" } };

// body セル基本 sx (必須未入力ハイライトを使わない列で、defaultColumn の base と同等の値を保持する)。
const BODY_BASE_SX = { minWidth: 0, overflow: "hidden" };

// ソート表示のみ (実ソートは未実装)。読み取り専用 / 並び替え対象の列に付与する
const SortHeader = ({ label }: { label: string }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <span>{label}</span>
    <IconFilterList fontSize={16} color={black[80]} />
  </Stack>
);

// 必須入力列。ラベル末尾に赤いアスタリスクを付ける (ソートアイコンは付けない)
const RequiredHeader = ({ label }: { label: string }) => (
  <Stack direction="row" alignItems="center" gap={0.25}>
    <span>{label}</span>
    <span style={{ color: red[400] }}>*</span>
  </Stack>
);

// 編集フィールド(Select/Input)を列幅いっぱいに広げる。flexセル内で content幅に縮まないようにする
const FieldCell: FC<{ children: React.ReactNode }> = ({ children }) => (
  // 編集モードでマウントされる入力(Select/Input/DatePicker)の高さを表示モード(lazy-field=33px)に揃え、クリック時の段差を防ぐ。
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      "& .MuiInputBase-root": { height: 33, minHeight: 33 },
      "& .MuiInputBase-input, & .MuiSelect-select": { paddingTop: 0, paddingBottom: 0 },
    }}
  >
    {children}
  </Box>
);

export const BulkImportLearnerTable: FC<Props> = ({ rows, onChangeRow, onDeleteRow, onSelectionChange, fieldErrors, focusTarget, clearSelectionNonce }) => {
  // InputDatePicker(セル内)が参照する useFormContext() 用のダミーフォーム。実値はセルが nonControl で管理する。
  const formMethods = useForm();

  // 不備行へスクロールするための行仮想化インスタンス参照。
  const rowVirtualizerRef = useRef<MRT_RowVirtualizer>(null);

  // 会社アカウントは所属機関の列を出さない (spec No.5/No.7)。
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;

  // 日本語能力の選択肢は master enum (EJapaneseLevelType) から取得 (value はコード値 n1..n5)。
  const { data: japaneseLevelOptions } = useEnum("EJapaneseLevelType");

  // 居住地: visa の都道府県を study マスタの正規名へ寄せて表示する (表示は都道府県のみ)。
  const { getProvinceName } = useGetMunicipalityCode();

  // 所属機関ドロップダウン用に RSO の会社一覧を取得 (会社アカウントは列自体を出さないので fetch しない)。
  // テーブルで1回だけ取得し、各行のセルへ options を渡す (行ごとの重複リクエストを避ける)。
  const { data: companyListData, isLoading: isLoadingCompanies } = useGetListCompanyByRso(!isCompanyUser);
  const companyOptions = useMemo<TOption[]>(
    () => (companyListData?.data ?? []).filter((c) => !c.isArchive).map((c) => ({ value: c.id, label: c.companyName })),
    [companyListData],
  );

  // 受験分野の選択肢(グローバル分野一覧)は table で1回だけ生成し、各行へ渡す。
  // 行ごとに hook 購読 + 一覧の transform を走らせるのを避ける (companyOptions と同じ方針)。
  const { industryOptions: baseIndustryOptions } = useLearnerIndustryOptions();

  // 取込行に含まれる受験分野(unique)の試験一覧を先読みする。仮想スクロールで各行のセルが初めて
  // 表示される際に都度フェッチして待たされる(スクロール時のローディング)のを防ぐ。
  const queryClient = useQueryClient();
  const uniqueIndustryIds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.industryId).filter((id): id is number => id != null))),
    [rows],
  );
  useEffect(() => {
    uniqueIndustryIds.forEach((industryId) => {
      queryClient.prefetchQuery({
        queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_EXAM_LIST, industryId],
        queryFn: () => getLearningProgressExamListApi({ industryId }),
        staleTime: MASTER_DATA_CACHE_TIME,
      });
    });
  }, [uniqueIndustryIds, queryClient]);

  // 一括変更(No.4)用チェックボックスは同一受験分野のみ選択可 (spec No.34)。
  // 最初に選択した行の受験分野(industryId)をロックし、それ以外の分野の行はチェック不可にする。
  // 受験分野が未選択(industryId=null)の行は、そもそもチェック不可にする。
  const [lockedField, setLockedField] = useState<number | null>(null);
  const enableRowSelection = useCallback(
    (row: MRT_Row<TBulkImportRow>) =>
      row.original.industryId != null && (lockedField === null || row.original.industryId === lockedField),
    [lockedField],
  );

  // 削除は確認モーダル経由 (spec No.28)。削除対象の行IDを保持する。
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const columns = useMemo<MRT_ColumnDef<TBulkImportRow>[]>(
    () => {
      const cols: MRT_ColumnDef<TBulkImportRow>[] = [
      { header: "氏名", id: "fullName", size: 170, Header: <SortHeader label="氏名" />, Cell: ({ row }) => row.original.fullName },
      {
        header: "所属機関",
        id: "companyName",
        // 名称が長くなりやすいため幅を広めに取る。
        size: 300,
        Header: <SortHeader label="所属機関" />,
        // 所属機関は RSO 内の会社一覧から選ぶドロップダウン。突合済みは既存会社を初期選択し、
        // 既存に無い (または「(新規)」を選んだ) 場合は visa 名称から新規 CompanyInRSO を作成する。
        Cell: ({ row }) => (
          <CompanyCell
            rowId={row.original.id}
            companyName={row.original.companyName}
            isNewCompany={row.original.isNewCompany}
            rsoCompanyId={row.original.rsoCompanyId ?? null}
            // 「(新規)」option のラベルに使う visa 由来の会社名。既存会社へ切替えても上書きされない company から取得する。
            visaCompanyName={row.original.company?.companyName ?? row.original.companyName}
            options={companyOptions}
            loading={isLoadingCompanies}
            onChangeRow={onChangeRow}
          />
        ),
      },
      {
        header: "受験分野",
        id: "industryId",
        size: 160,
        Header: <RequiredHeader label="受験分野" />,
        Cell: ({ row }) => (
          <ExamFieldCell
            rowId={row.original.id}
            industryId={row.original.industryId ?? null}
            industryName={row.original.industryName ?? null}
            baseOptions={baseIndustryOptions}
            onChangeRow={onChangeRow}
          />
        ),
      },
      { header: "国籍", id: "nationality", size: 110, Header: <SortHeader label="国籍" />, Cell: ({ row }) => row.original.nationality },
      { header: "性別", id: "gender", size: 72, Header: <SortHeader label="性別" />, Cell: ({ row }) => row.original.gender },
      {
        header: "ステータス",
        id: "status",
        size: 134,
        Cell: ({ row }) => <StatusCell rowId={row.original.id} value={row.original.status} onChangeRow={onChangeRow} />,
      },
      {
        header: "1号残期間",
        id: "remainingPeriod",
        size: 110,
        Header: <SortHeader label="1号残期間" />,
        // 1号起算日 + ブランク期間 から自動計算して表示する (create フォームと同一ロジック / 読み取り専用)。
        Cell: ({ row }) => (
          <RemainingPeriodCell clause1StartDate={row.original.clause1StartDate} blankPeriodMonths={row.original.blankPeriodMonths ?? null} />
        ),
      },
      {
        header: "居住地",
        id: "residence",
        size: 130,
        Header: <SortHeader label="居住地" />,
        // 居住地は 都道府県(province) のみ表示する (市区町村は保存のみで表示しない)。マスタ正規名へ寄せる。
        Cell: ({ row }) => (row.original.province ? getProvinceName(row.original.province) : ""),
      },
      {
        header: "日本語能力",
        id: "japaneseLevel",
        size: 110,
        muiTableHeadCellProps: { sx: LEFT_HEAD_SX },
        Cell: ({ row }) => (
          <JapaneseLevelCell rowId={row.original.id} value={row.original.japaneseLevel} options={japaneseLevelOptions} onChangeRow={onChangeRow} />
        ),
      },
      {
        header: "前回点数",
        id: "lastScore",
        size: 110,
        muiTableHeadCellProps: { sx: LEFT_HEAD_SX },
        Cell: ({ row }) => <LastScoreCell rowId={row.original.id} value={row.original.lastScore} onChangeRow={onChangeRow} />,
      },
      {
        header: "手続き管理",
        id: "procedureManagement",
        size: 110,
        Header: <RequiredHeader label="手続き管理" />,
        // align:"center" は MRT が grid レイアウトのセル整列に使う(sx の justifyContent では効かない)。header/body 両方に付けて列全体を中央寄せにする。
        muiTableHeadCellProps: { align: "center", sx: HEAD_BASE_SX },
        muiTableBodyCellProps: { align: "center", sx: BODY_BASE_SX },
        Cell: ({ row }) => (
          <SwitchCell rowId={row.original.id} field="procedureManagement" checked={row.original.procedureManagement} onChangeRow={onChangeRow} />
        ),
      },
      {
        header: "学習アプリ",
        id: "learningApp",
        size: 100,
        Header: <RequiredHeader label="学習アプリ" />,
        muiTableHeadCellProps: { align: "center", sx: HEAD_BASE_SX },
        muiTableBodyCellProps: { align: "center", sx: BODY_BASE_SX },
        Cell: ({ row }) => (
          <SwitchCell
            rowId={row.original.id}
            field="learningApp"
            checked={row.original.learningApp}
            onChangeRow={onChangeRow}
            // 学習アプリ OFF ではカリキュラム開始日のみクリアする (下でカリキュラム開始日セルのみ disable される)。
            // 受験予定 / 受験予定年月 は学習アプリの ON/OFF に影響されない (クリア・disable しない)。
            patchOnOff={{ curriculumStartDate: "" }}
            // 学習アプリ ON でカリキュラム開始日を既定値(翌週 or 当週の月曜)に戻す (手動フォーム LearnerCurriculumForm と同挙動)。
            patchOnOn={{ curriculumStartDate: getNextOrCurrentMonday().format("YYYY/MM/DD") }}
          />
        ),
      },
      {
        header: "カリキュラム開始日",
        id: "curriculumStartDate",
        size: 160,
        Header: <RequiredHeader label="カリキュラム開始日" />,
        // 学習アプリ OFF の行は編集不可。
        Cell: ({ row }) => (
          <CurriculumStartDateCell
            rowId={row.original.id}
            value={row.original.curriculumStartDate}
            disabled={!row.original.learningApp}
            onChangeRow={onChangeRow}
          />
        ),
      },
      {
        header: "受験予定",
        id: "examId",
        size: 200,
        Header: <RequiredHeader label="受験予定" />,
        Cell: ({ row }) => (
          <ExamScheduleCell
            rowId={row.original.id}
            industryId={row.original.industryId ?? null}
            value={row.original.examId ?? null}
            // 受験予定は学習アプリの ON/OFF に影響されない (常に編集可)。
            onChangeRow={onChangeRow}
          />
        ),
      },
      {
        header: "受験予定年月",
        id: "examScheduleYm",
        size: 140,
        Header: <RequiredHeader label="受験予定年月" />,
        Cell: ({ row }) => (
          <ExamScheduleYmCell
            rowId={row.original.id}
            industryId={row.original.industryId ?? null}
            examId={row.original.examId ?? null}
            value={row.original.examScheduleYm}
            // 受験予定年月は学習アプリの ON/OFF に影響されない (常に編集可)。
            onChangeRow={onChangeRow}
          />
        ),
      },
      {
        header: "",
        id: "actions",
        size: 48,
        enableSorting: false,
        Cell: ({ row }) => (
          <DeleteButton onClick={() => setPendingDeleteId(row.original.id)}>
            <IconDeleteOutline width={20} height={20} />
          </DeleteButton>
        ),
      },
      ];
      // 会社アカウントは自社のみのため所属機関の列を出さない (spec No.5/No.7)
      return cols.filter((col) => !(isCompanyUser && col.id === "companyName"));
    },
    [onChangeRow, japaneseLevelOptions, isCompanyUser, companyOptions, isLoadingCompanies, baseIndustryOptions, getProvinceName],
  );

  // 必須未入力セルのハイライト用 cell props。fieldErrors が変わった時だけ再生成し、
  // 毎レンダリングで関数が作り直されて MRT が全列を再処理するのを防ぐ。
  const defaultColumn = useMemo(
    () => ({
      muiTableBodyCellProps: ({ row, cell }: { row: MRT_Row<TBulkImportRow>; cell: MRT_Cell<TBulkImportRow> }) => {
        const base = { minWidth: 0, overflow: "hidden" };
        const cols = fieldErrors?.[row.original.id as number];
        if (cols?.includes(cell.column.id)) {
          return {
            sx: {
              ...base,
              // 編集モード(MUI入力)と表示モード(.lazy-field)両方の枠線を赤くする。
              "& .MuiOutlinedInput-notchedOutline": { borderColor: `${red[400]} !important`, borderWidth: "1.5px !important" },
              "& .lazy-field": { borderColor: `${red[400]} !important`, borderWidth: "1.5px !important" },
            },
          };
        }
        return { sx: base };
      },
    }),
    [fieldErrors],
  );

  const { table, rowSelectionKeys, clearSelection } = useSelectionTable({
    primaryKey: "id",
    muiTableHeadCellProps: { sx: HEAD_BASE_SX },
    columns,
    rows,
    // 行選択チェックボックスを有効化(同一受験分野のみ)。チェックボックス列は左端に固定する
    enableRowSelection,
    // ヘッダーの「すべて選択」チェックボックスは表示しない
    enableSelectAll: false,
    // 選択列ヘッダーの既定文言「Select」を非表示にする。
    // size を 56px に固定: enableSelectAll=false だと MRT の選択列既定サイズが 70px になり、
    // body セル(チェックボックスを持つ)は共通CSS `:has(.MuiCheckbox-root)` で 56px に強制されるため、
    // ヘッダー(70px)と body(56px)の幅がズレ、後続の全列がヘッダーと body で横にずれてしまう。
    // ヘッダー側も 56px に揃えて整列させる。
    displayColumnDefOptions: { "mrt-row-select": { header: "", size: 56, minSize: 56, maxSize: 56 } },
    pinningColumnsLeft: ["mrt-row-select"],
    // 削除ボタン列を右端に固定。横スクロールしても常に表示される
    pinningColumns: ["actions"],
    // grid-no-grow: 列幅を size 固定にして、Select選択後に列が伸びる(full column)のを防ぐ
    layoutMode: "grid-no-grow",
    // 行仮想化: 可視範囲の行のみ描画する。取込件数が多くても重い MUI セル(Select / DatePicker)の
    // マウント数を抑え、編集時の再レンダリングコストを大幅に下げる (パフォーマンス対策)。
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef: rowVirtualizerRef,
    // 行が軽く(A1: render-on-demand)なったので overscan を広げ、可視範囲外も多めに先描画して
    // 高速スクロール時の白い隙間を抑える。
    rowVirtualizerOptions: { overscan: 20, estimateSize: () => 53 },
    localization: SELECT_LOCALIZATION,
    // 必須未入力セルをハイライトする (fieldErrors に含まれる rowId × カラムID)。
    // td 全体ではなく、内部の入力要素(Select / DatePicker)の枠線だけを赤くする。
    defaultColumn,
    renderEmptyRowsFallback: () => <Box />,
  });

  // 検証で不備が出たら先頭の不備行へスクロールする (nonce 変化のたびに実行)。
  useEffect(() => {
    if (!focusTarget) return;
    const index = rows.findIndex((r) => r.id === focusTarget.rowId);
    if (index >= 0) rowVirtualizerRef.current?.scrollToIndex(index, { align: "center" });
  }, [focusTarget?.nonce]);

  // 選択IDを親へ通知。選択が変わったときだけ実行する(セル編集のたびに親を再レンダリングさせない=パフォーマンス対策)。
  useEffect(() => {
    onSelectionChange?.(rowSelectionKeys);
  }, [rowSelectionKeys, onSelectionChange]);

  // 親から解除指示(nonce 変化)が来たら全選択をクリアする。一括変更の確定後にチェックを外す用途。
  // 初期値(0/undefined)では実行しない (マウント時に空選択をクリアして無駄に再レンダリングするのを防ぐ)。
  useEffect(() => {
    if (!clearSelectionNonce) return;
    clearSelection();
  }, [clearSelectionNonce]);

  // 選択中の行の受験分野(industryId)をロック (No.34 の同一分野制限用)。行データ変化でも再計算するが、同値の setState は no-op。
  useEffect(() => {
    const selectedRows = rows.filter((r) => rowSelectionKeys.includes(String(r.id)));
    setLockedField(selectedRows.length > 0 ? (selectedRows[0].industryId ?? null) : null);
  }, [rowSelectionKeys, rows]);

  // 編集フィールド(Select/Input)があるため、行高はシステム既定より高い 53px にする。
  // InputDatePicker は useFormContext() を参照するため、ダミーの FormProvider で包む(セルは nonControl で自前管理)。
  return (
    <FormProvider {...formMethods}>
      <Table table={table} isLoading={false} height="100%" minHeight={200} bodyRowHeight={53} borderColor={divider.high} />
      <ConfirmDeleteModal
        open={pendingDeleteId !== null}
        message="行を削除してよろしいですか？"
        onClose={() => setPendingDeleteId(null)}
        onOk={() => {
          if (pendingDeleteId !== null) onDeleteRow(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </FormProvider>
  );
};

// --- メモ化セル ---------------------------------------------------------
// 1セルの編集で rows 全体が再生成され MRT が全行を再レンダリングする。各セルを React.memo 化し、
// 安定した onChangeRow + プリミティブ props を渡すことで、値が変わった行のセルだけ再描画させる。
type OnChangeRow = (id: number, patch: Partial<TBulkImportRow>) => void;

// A1 (render-on-demand) 用の軽量表示。Select/DatePicker をマウントせず、選択値のテキストだけを表示する。
// クリックで初めて重い入力をマウントするため、行のマウントコストを大幅に下げ、高速スクロール時の白画面を防ぐ。
// className="lazy-field" は必須未入力時の赤枠(defaultColumn)で参照する。
const LazyFieldDisplay: FC<{
  label?: string | null;
  placeholder: string;
  disabled?: boolean;
  // 値が不正な場合に枠線を赤くする (前回点数の即時バリデーション用)。
  error?: boolean;
  icon?: ReactNode;
  onActivate: () => void;
}> = ({ label, placeholder, disabled, error, icon, onActivate }) => (
  <Box
    className="lazy-field"
    onClick={disabled ? undefined : onActivate}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 0.5,
      height: 33,
      px: 1.5,
      boxSizing: "border-box",
      border: `1px solid ${error ? red[400] : border.middle}`,
      borderRadius: 1,
      backgroundColor: disabled ? divider.low : background.white,
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: 14,
      color: label ? text.primary : text.tertiary,
    }}
  >
    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {label || placeholder}
    </Box>
    {icon ?? <IconExpandMore fontSize={20} color={text.tertiary} />}
  </Box>
);

// 遅延 Select セル: 既定は軽量テキスト、クリックで Select をマウントしドロップダウンを開く。選択/外クリックで表示へ戻す。
const LazySelectCell: FC<{
  value: string | number;
  options: TOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  displayLabel?: string;
  onChange: (_value: string) => void;
}> = ({ value, options, placeholder = "選択…", disabled, loading, clearable, displayLabel, onChange }) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    const label = displayLabel ?? options.find((o) => String(o.value) === String(value))?.label;
    return (
      <FieldCell>
        <LazyFieldDisplay label={label} placeholder={placeholder} disabled={disabled} onActivate={() => setEditing(true)} />
      </FieldCell>
    );
  }
  return (
    <FieldCell>
      <Select
        placeholder={placeholder}
        options={options}
        value={value}
        disabled={disabled}
        loading={loading}
        clearable={clearable}
        sx={SELECT_NO_FOCUS_RING}
        autoFocus
        defaultOpen
        onClose={() => setEditing(false)}
        onChange={onChange}
      />
    </FieldCell>
  );
};

// 遅延 DatePicker セル: 既定は日付テキスト、クリックで DatePicker をマウントしカレンダーを開く。閉じたら表示へ戻す。
const LazyDateCell: FC<{
  value: string;
  format: string;
  views?: ("year" | "month" | "day")[];
  openTo?: "year" | "month" | "day";
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  onChange: (_value: Dayjs | null) => void;
}> = ({ value, format, views, openTo, disabled, readonly, placeholder = "選択…", onChange }) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <FieldCell>
        <LazyFieldDisplay
          label={value || undefined}
          placeholder={placeholder}
          // readonly(PT自動補完)や disabled は編集モードへ入らせない。
          disabled={disabled || readonly}
          icon={<IconCalendar />}
          onActivate={() => setEditing(true)}
        />
      </FieldCell>
    );
  }
  return (
    <FieldCell>
      <InputDatePicker
        nonControl
        format={format}
        views={views}
        openTo={openTo}
        disabled={disabled}
        readonly={readonly}
        // マウント時にカレンダーを開き、閉じたら表示モードへ戻す (open/onClose は rest で内部状態を上書きする)。
        open
        onClose={() => setEditing(false)}
        value={value ? dayjs(value, format) : null}
        onChange={onChange}
      />
    </FieldCell>
  );
};

// 遅延テキスト入力セル: 既定は値のテキスト、クリックで Input をマウントしフォーカス。blur で表示へ戻す。
// error: 値が不正な場合に表示/編集どちらのモードでも枠線を赤くする (即時バリデーション用)。
const LazyInputCell: FC<{ value: string; placeholder?: string; error?: boolean; onChange: (_value: string) => void }> = ({
  value,
  placeholder = "未入力",
  error,
  onChange,
}) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <FieldCell>
        <LazyFieldDisplay label={value || undefined} placeholder={placeholder} error={error} icon={<></>} onActivate={() => setEditing(true)} />
      </FieldCell>
    );
  }
  return (
    <FieldCell>
      <Input autoFocus error={error} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} onBlur={() => setEditing(false)} />
    </FieldCell>
  );
};

// 受験分野セレクト。選択肢は global の分野一覧(/industries)を table で1回だけ生成し baseOptions として受け取る。
// BE が認証ユーザー単位で一覧を絞り込む(RSO/会社の hiddenIndustryIds, hasLearner)ため、FE では会社による絞り込みを行わない。
// 突合済みの industryId は industryName をフォールバック選択肢にして、マスタ読込前でも表示が消えないようにする。
const ExamFieldCell = memo(
  ({
    rowId,
    industryId,
    industryName,
    baseOptions,
    onChangeRow,
  }: {
    rowId: number;
    industryId: number | null;
    industryName: string | null;
    baseOptions: TOption[];
    onChangeRow: OnChangeRow;
  }) => {
    // 突合済みの分野が一覧に無い(非表示等)場合はフォールバックで補い、表示が消えないようにする。
    const options = useMemo<TOption[]>(() => {
      if (industryId != null && industryName && !baseOptions.some((o) => String(o.value) === String(industryId))) {
        return [...baseOptions, { label: industryName, value: industryId }];
      }
      return baseOptions;
    }, [baseOptions, industryId, industryName]);

    return (
      <LazySelectCell
        value={industryId ?? ""}
        options={options}
        onChange={(v) => {
          // 受験分野を変えると試験一覧が変わるため、受験予定(examId)・受験予定年月をリセット。表示名も更新する。
          const nextId = v ? Number(v) : null;
          const nextName = options.find((o) => String(o.value) === String(v))?.label ?? null;
          onChangeRow(rowId, { industryId: nextId, industryName: nextName, examId: null, examScheduleYm: "" });
        }}
      />
    );
  },
);
ExamFieldCell.displayName = "ExamFieldCell";

// 「(新規)」option の値。既存会社の id (数値) と衝突しない文字列センチネルにする。
const NEW_COMPANY_VALUE = "__NEW__";

// 所属機関セレクト。選択肢は RSO 内の会社一覧 + visa 名称から新規作成する「(新規)」option。
// 既存と同名でも「(新規)」は常に表示する (= 同名2件: 既存 + 新規)。突合済みは既存会社を初期選択する。
const CompanyCell = memo(
  ({
    rowId,
    companyName,
    isNewCompany,
    rsoCompanyId,
    visaCompanyName,
    options,
    loading,
    onChangeRow,
  }: {
    rowId: number;
    companyName: string;
    isNewCompany: boolean;
    rsoCompanyId: number | null;
    visaCompanyName: string;
    options: TOption[];
    loading: boolean;
    onChangeRow: OnChangeRow;
  }) => {
    const mergedOptions = useMemo<TOption[]>(() => {
      const base = [...options];
      // 突合済みの既存会社が options に無い (archive 等) 場合のフォールバック。表示が消えないようにする。
      if (!isNewCompany && rsoCompanyId != null && !base.some((o) => String(o.value) === String(rsoCompanyId))) {
        base.push({ value: rsoCompanyId, label: companyName });
      }
      // visa 由来の会社名から新規作成する option を常に末尾へ追加する。
      if (visaCompanyName) base.push({ value: NEW_COMPANY_VALUE, label: `${visaCompanyName} (新規)` });
      return base;
    }, [options, isNewCompany, rsoCompanyId, companyName, visaCompanyName]);

    const value = isNewCompany ? NEW_COMPANY_VALUE : (rsoCompanyId ?? "");

    return (
      <LazySelectCell
        value={value}
        options={mergedOptions}
        clearable={false}
        loading={loading}
        onChange={(v) => {
          if (v === NEW_COMPANY_VALUE) {
            // 新規作成: rsoCompanyId をクリアし、保存時は companyName を newCompanyName として送る。
            onChangeRow(rowId, { isNewCompany: true, rsoCompanyId: null, companyName: visaCompanyName });
          } else {
            const id = Number(v);
            const label = options.find((o) => String(o.value) === String(v))?.label ?? "";
            onChangeRow(rowId, { isNewCompany: false, rsoCompanyId: id, companyName: label });
          }
        }}
      />
    );
  },
);
CompanyCell.displayName = "CompanyCell";

const StatusCell = memo(({ rowId, value, onChangeRow }: { rowId: number; value: string; onChangeRow: OnChangeRow }) => (
  <FieldCell>
    <LearnerPlanStatusSelect value={value} options={STATUS_OPTIONS} onChange={(v) => onChangeRow(rowId, { status: v })} />
  </FieldCell>
));
StatusCell.displayName = "StatusCell";

const JapaneseLevelCell = memo(
  ({ rowId, value, options, onChangeRow }: { rowId: number; value: string; options: TOption[]; onChangeRow: OnChangeRow }) => {
    // enum 未ロード中や visa取込の自由入力値(コード値でない)は選択肢に無いため、一致しない値は "" にして out-of-range warning を防ぐ。
    const safeValue = options.some((o) => String(o.value) === String(value)) ? value : "";
    return <LazySelectCell value={safeValue} options={options} onChange={(v) => onChangeRow(rowId, { japaneseLevel: (v as string) ?? "" })} />;
  },
);
JapaneseLevelCell.displayName = "JapaneseLevelCell";

// 前回点数。create learner と同じ 0〜1000 の整数制限。値が範囲外/非整数なら blur 後に即赤枠表示する
// (submit を待たない)。最終的な保存ブロック・トーストは validateBeforeSave 側でも行う。
const LastScoreCell = memo(({ rowId, value, onChangeRow }: { rowId: number; value: string; onChangeRow: OnChangeRow }) => (
  <LazyInputCell value={value} placeholder="未入力" error={isLastScoreValueInvalid(value)} onChange={(v) => onChangeRow(rowId, { lastScore: v })} />
));
LastScoreCell.displayName = "LastScoreCell";

const SwitchCell = memo(
  ({
    rowId,
    field,
    checked,
    onChangeRow,
    patchOnOff,
    patchOnOn,
  }: {
    rowId: number;
    field: "procedureManagement" | "learningApp";
    checked: boolean;
    onChangeRow: OnChangeRow;
    // OFF(false)へ切替えた時に併せて適用する追加パッチ (学習アプリ OFF でカリキュラム関連項目をクリアする用途)。
    patchOnOff?: Partial<TBulkImportRow>;
    // ON(true)へ切替えた時に併せて適用する追加パッチ (学習アプリ ON でカリキュラム開始日を既定値に戻す用途)。
    patchOnOn?: Partial<TBulkImportRow>;
  }) => (
    // BulkImportCompleteTable と同じ中央寄せ手法: width:100% で埋めると justifyContent が効かないため、
    // fit-content + mx:auto で grid セル(flex)の余白を左右 auto マージンに吸わせて確実に中央へ置く。
    <Stack alignItems="center" justifyContent="center" sx={{ width: "fit-content", mx: "auto", ...SWITCH_CELL_SX }}>
      <Switch nonControl checked={checked} onChange={(_, c) => onChangeRow(rowId, { [field]: c, ...(c ? patchOnOn : patchOnOff) })} />
    </Stack>
  ),
);
SwitchCell.displayName = "SwitchCell";

// 1号残期間 (自動計算 / 読み取り専用)。create フォームと同じ calculateResidencePeriods を使い、
// 1号起算日(clause1StartDate) + ブランク期間(blankPeriodMonths) から残期間を算出する。
// 起算日が未設定/未来の場合は空表示(emptyValue=""), 期限切れは "0年0か月"。
const RemainingPeriodCell = memo(
  ({ clause1StartDate, blankPeriodMonths }: { clause1StartDate: string; blankPeriodMonths: number | null }) => {
    const { remaining } = useMemo(
      () => calculateResidencePeriods(clause1StartDate || null, blankPeriodMonths, ""),
      [clause1StartDate, blankPeriodMonths],
    );
    return <Box sx={{ width: "100%", textAlign: "center" }}>{remaining}</Box>;
  },
);
RemainingPeriodCell.displayName = "RemainingPeriodCell";

const CurriculumStartDateCell = memo(
  ({ rowId, value, disabled, onChangeRow }: { rowId: number; value: string; disabled?: boolean; onChangeRow: OnChangeRow }) => (
    <LazyDateCell
      value={value}
      format="YYYY/MM/DD"
      disabled={disabled}
      onChange={(v) => onChangeRow(rowId, { curriculumStartDate: isDayjs(v) && v.isValid() ? v.format("YYYY/MM/DD") : "" })}
    />
  ),
);
CurriculumStartDateCell.displayName = "CurriculumStartDateCell";

// 受験予定セレクト。選択中の受験分野(industryId)の試験一覧を実API(/learning-progress/industry/:id/exams)から取得。
// 選択肢は active な試験のみ(選択中の examId は status 問わず保持して表示が消えないようにする)。
const ExamScheduleCell = memo(
  ({
    rowId,
    industryId,
    value,
    disabled,
    onChangeRow,
  }: {
    rowId: number;
    industryId: number | null;
    value: number | null;
    disabled?: boolean;
    onChangeRow: OnChangeRow;
  }) => {
    const { data } = useLearningProgressExamList({ industryId: industryId ?? 0 });
    const exams = useMemo(() => data?.data ?? [], [data]);
    const options = useMemo<TOption[]>(
      () => exams.filter((e) => e.status === EXAM_STATUS.ACTIVE || e.id === value).map((e) => ({ value: e.id, label: e.periodName })),
      [exams, value],
    );
    return (
      <LazySelectCell
        placeholder="未選択"
        options={options}
        value={value ?? ""}
        // 学習アプリ OFF、または No.9(受験分野)が未選択なら disable
        disabled={disabled || industryId == null}
        onChange={(v) => {
          const examId = v ? Number(v) : null;
          const exam = exams.find((e) => e.id === examId);
          // PT(筆記)の試験は受験予定年月を examMonth で自動補完、それ以外(CBT)は空にして手入力させる
          const examScheduleYm = exam?.examMethod === EXAM_METHOD.PAPER_TEST && exam.examMonth ? dayjs(exam.examMonth).format("YYYY/MM") : "";
          onChangeRow(rowId, { examId, examScheduleYm });
        }}
      />
    );
  },
);
ExamScheduleCell.displayName = "ExamScheduleCell";

const ExamScheduleYmCell = memo(
  ({
    rowId,
    industryId,
    examId,
    value,
    disabled,
    onChangeRow,
  }: {
    rowId: number;
    industryId: number | null;
    examId: number | null;
    value: string;
    disabled?: boolean;
    onChangeRow: OnChangeRow;
  }) => {
    const { data } = useLearningProgressExamList({ industryId: industryId ?? 0 });
    const exam = useMemo(() => (data?.data ?? []).find((e) => e.id === examId), [data, examId]);
    const isPaperTest = exam?.examMethod === EXAM_METHOD.PAPER_TEST;
    return (
      <LazyDateCell
        value={value}
        format="YYYY/MM"
        views={["year", "month"]}
        openTo="month"
        // 学習アプリ OFF、または No.26(受験予定)が未選択なら disable。PT(筆記)は自動補完済みで編集不可、CBT は手入力
        disabled={disabled || examId == null}
        readonly={isPaperTest}
        onChange={(v) => onChangeRow(rowId, { examScheduleYm: isDayjs(v) && v.isValid() ? v.format("YYYY/MM") : "" })}
      />
    );
  },
);
ExamScheduleYmCell.displayName = "ExamScheduleYmCell";

const DeleteButton: FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: 1,
      border: `1px solid ${border.middle}`,
      color: red[400],
      cursor: "pointer",
      "&:hover": { backgroundColor: red[99] },
    }}
  >
    {children}
  </Box>
);
